import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Alert, Box, Snackbar, useMediaQuery, useTheme } from "@mui/material";
import TeamRightRail from "./components/TeamRightRail";
import { getApiErrorMessage } from "../../utils/apiErrorMessage";
import {
  useTeamSpaces,
  useTeamSpaceDetails,
  useTeamSpaceAdmin,
} from "../../hooks/useTeamSpaces";
import { useTeamMessages } from "../../hooks/useTeamMessages";
import { sendTeamMessage } from "../../api/client";
import { useRealtimeTeamMessages } from "../../hooks/useRealtimeTeamMessages";
import { useAppUnreadBadge } from "../../hooks/useAppUnreadBadge";
import { useEffectivePermissions, scopeSetFromPermissions } from "../../hooks/useEffectivePermissions";
import { useNotificationsOptional } from "../../context/NotificationContext";
import SpaceListPane from "./panes/SpaceListPane";
import TeamMessagePane from "./panes/TeamMessagePane";
import { resolveTeamSpaceLeadId, getTeamPaneContainerStyle, TEAM_MOBILE_MEDIA_QUERY, isUserToUserTeamDm } from "./utils/teamUiUtils";
import { storeTeamScrollToMessage } from "./utils/teamMessageUtils";
import { readTabUserData } from "../../utils/tabAuthStorage";

const TeamPage = ({ basePath = "/equipe", standalone = false }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(TEAM_MOBILE_MEDIA_QUERY);
  const isCompactLayout = isMobile;
  const isEdgeToEdge = isCompactLayout || standalone;
  const navigate = useNavigate();
  const { id: routeSpaceId } = useParams();

  const userData = useMemo(() => readTabUserData() || {}, []);
  const currentUserId = userData?.id;
  const isMasterUser = Number(userData?.master) === 1;
  const [errorMsg, setErrorMsg] = useState("");

  const { data: permissionsData } = useEffectivePermissions();
  const scopes = scopeSetFromPermissions(permissionsData, userData);
  const notificationsCtx = useNotificationsOptional();

  const {
    spaces,
    refetch: refetchSpaces,
    createChannel,
    createDm,
    markRead,
    deleteSpace,
    isCreating,
    isDeleting,
  } = useTeamSpaces();

  const {
    addMember,
    removeMember,
    updateSpace,
    isAddingMember,
    isRemovingMember,
    isUpdating,
  } = useTeamSpaceAdmin();

  const selectedSpaceId = routeSpaceId ? Number(routeSpaceId) : null;
  const listedSpace = useMemo(
    () => spaces.find((s) => Number(s.id) === Number(selectedSpaceId)) || null,
    [spaces, selectedSpaceId]
  );

  useEffect(() => {
    if (!isMasterUser || !selectedSpaceId || !spaces.length) return;
    const space = spaces.find((s) => Number(s.id) === Number(selectedSpaceId));
    if (!space || isUserToUserTeamDm(space)) {
      navigate(basePath, { replace: true });
    }
  }, [isMasterUser, selectedSpaceId, spaces, navigate, basePath]);

  const unreadTotal = useMemo(
    () => (spaces || []).reduce((sum, s) => sum + (Number(s.unreadCount) || 0), 0),
    [spaces]
  );
  useAppUnreadBadge(standalone ? unreadTotal : 0);

  const { data: spaceDetails } = useTeamSpaceDetails(selectedSpaceId);
  const members = spaceDetails?.members || [];
  const selectedSpace = listedSpace || spaceDetails?.space || null;

  const {
    messages,
    loadMessages,
    loadMore,
    syncLatest,
    syncRecentMessages,
    sendMessage,
    editMessage,
    deleteMessage,
    isSending,
    isLoading,
    loadError,
    hasMore,
    reset,
  } = useTeamMessages(selectedSpaceId);

  useEffect(() => {
    reset();
    if (!selectedSpaceId) return;
    loadMessages().catch((err) => {
      setErrorMsg(getApiErrorMessage(err, "Não foi possível carregar as mensagens."));
    });
    markRead(selectedSpaceId)
      .catch(() => {})
      .finally(() => refetchSpaces());
  }, [selectedSpaceId, reset, loadMessages, markRead, refetchSpaces]);

  const setTeamPageFocus = notificationsCtx?.setTeamPageFocus;

  useEffect(() => {
    setTeamPageFocus?.(selectedSpaceId ?? null);
    return () => setTeamPageFocus?.(null);
  }, [setTeamPageFocus, selectedSpaceId]);

  useRealtimeTeamMessages({
    selectedSpaceId,
    syncLatestMessages: syncLatest,
    refetchSpaces,
    markRead,
  });

  const handleRefreshSpaces = useCallback(async () => {
    const tasks = [refetchSpaces()];
    if (selectedSpaceId) {
      tasks.push(syncLatest({ refreshRecent: true }));
    }
    await Promise.allSettled(tasks);
  }, [refetchSpaces, selectedSpaceId, syncLatest]);

  const handleSelectSpace = useCallback(
    (spaceId) => {
      navigate(`${basePath}/${spaceId}`);
    },
    [navigate, basePath]
  );

  const handleCreateChannel = useCallback(
    async (payload) => {
      const res = await createChannel(payload);
      await refetchSpaces();
      if (res?.id) navigate(`${basePath}/${res.id}`);
      return res;
    },
    [createChannel, navigate, refetchSpaces, basePath]
  );

  const handleSend = useCallback(
    async (payload) => {
      if (payload?.editMessageId) {
        const { editMessageId, ...rest } = payload;
        await editMessage({ messageId: editMessageId, ...rest });
        await syncLatest({ refreshRecent: true });
      } else {
        await sendMessage(payload);
        await syncLatest();
      }
    },
    [sendMessage, editMessage, syncLatest]
  );

  const handleDeleteMessage = useCallback(
    async (messageId) => {
      await deleteMessage(messageId);
      await syncLatest({ refreshRecent: true });
    },
    [deleteMessage, syncLatest]
  );

  const handleNavigateToQuotedSpace = useCallback(
    (targetSpaceId, messageId) => {
      if (!targetSpaceId) return;
      storeTeamScrollToMessage(targetSpaceId, messageId);
      navigate(`${basePath}/${targetSpaceId}`);
    },
    [navigate, basePath]
  );

  const handleForwardMessage = useCallback(
    async (payload) => {
      await sendTeamMessage(payload);
      if (Number(payload?.spaceId) === Number(selectedSpaceId)) {
        await syncLatest();
      }
      await refetchSpaces();
    },
    [selectedSpaceId, syncLatest, refetchSpaces]
  );

  const leadId = useMemo(
    () => resolveTeamSpaceLeadId(selectedSpace, messages),
    [selectedSpace, messages]
  );

  const currentUserMember = useMemo(
    () =>
      members.find(
        (m) =>
          m.participantType === "user" &&
          String(m.participantId) === String(currentUserId)
      ),
    [members, currentUserId]
  );

  const canManageChannel = useMemo(() => {
    if (!selectedSpace || selectedSpace.type === "dm") return false;
    if (scopes.has("team_chat.manage_channels")) return true;
    return currentUserMember?.role === "owner" || currentUserMember?.role === "admin";
  }, [selectedSpace, scopes, currentUserMember]);

  const isAdminBusy = isAddingMember || isRemovingMember || isUpdating || isDeleting;

  const handleUpdateSpace = useCallback(
    async (payload) => {
      if (!selectedSpaceId) return;
      await updateSpace({ spaceId: selectedSpaceId, payload });
      await refetchSpaces();
    },
    [selectedSpaceId, updateSpace, refetchSpaces]
  );

  const handleAddMember = useCallback(
    async (payload) => {
      if (!selectedSpaceId) return;
      await addMember({ spaceId: selectedSpaceId, payload });
    },
    [selectedSpaceId, addMember]
  );

  const handleRemoveMember = useCallback(
    async (memberId) => {
      if (!selectedSpaceId) return;
      await removeMember({ spaceId: selectedSpaceId, memberId });
      await refetchSpaces();
    },
    [selectedSpaceId, removeMember, refetchSpaces]
  );

  const handleDeleteSpace = useCallback(async () => {
    if (!selectedSpaceId) return;
    await deleteSpace(selectedSpaceId);
    await refetchSpaces();
    navigate(basePath);
  }, [selectedSpaceId, deleteSpace, refetchSpaces, navigate, basePath]);

  const showList = !isCompactLayout || !selectedSpaceId;
  const showThread = !isCompactLayout || Boolean(selectedSpaceId);
  const isSinglePaneThread = showThread && !showList;

  const containerStyle = useMemo(
    () => getTeamPaneContainerStyle(theme, isCompactLayout),
    [theme, isCompactLayout]
  );

  return (
    <Box
      className="tour-team-layout"
      sx={{
        display: "flex",
        flexDirection: isCompactLayout ? "column" : "row",
        width: "100%",
        maxWidth: "100%",
        flex: 1,
        minHeight: standalone ? "100%" : 0,
        height: standalone ? "100%" : undefined,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          position: "relative",
          gap: isEdgeToEdge ? 0 : 1,
          pt: isEdgeToEdge ? 0 : 2,
          px: isEdgeToEdge ? 0 : 1,
          pb: isEdgeToEdge ? 0 : isCompactLayout ? 0 : 1,
        }}
      >
      {showList ? (
        <SpaceListPane
          containerStyle={containerStyle}
          isMobile={isCompactLayout}
          spaces={spaces}
          selectedSpaceId={selectedSpaceId}
          onSelectSpace={handleSelectSpace}
          onCreateChannel={handleCreateChannel}
          onCreateDm={createDm}
          isCreating={isCreating}
          currentUserId={currentUserId}
          basePath={basePath}
          showOpenPopupButton={!standalone}
          onRefresh={handleRefreshSpaces}
        />
      ) : null}

      {showThread ? (
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            maxWidth: "100%",
            display: "flex",
            flexDirection: "column",
            width: isCompactLayout ? "100%" : undefined,
            overflow: "hidden",
          }}
        >
          <TeamMessagePane
          containerStyle={containerStyle}
          isMobile={isCompactLayout}
          space={selectedSpace}
          members={members}
          spaces={spaces}
          messages={messages}
          currentUserId={currentUserId}
          onSend={handleSend}
          onDeleteMessage={handleDeleteMessage}
          onForwardMessage={handleForwardMessage}
          onSyncRecentMessages={syncRecentMessages}
          isSending={isSending}
          isLoading={isLoading}
          loadError={loadError}
          hasMore={hasMore}
          onLoadMore={loadMore}
          leadId={leadId}
          onBack={isSinglePaneThread ? () => navigate(basePath) : undefined}
          canManageChannel={canManageChannel}
          onUpdateSpace={handleUpdateSpace}
          onAddMember={handleAddMember}
          onRemoveMember={handleRemoveMember}
          onDeleteSpace={handleDeleteSpace}
          isAdminBusy={isAdminBusy}
          standalone={standalone}
          onNavigateToQuotedSpace={handleNavigateToQuotedSpace}
        />
        </Box>
      ) : null}
      </Box>

      {!isCompactLayout || standalone ? (
        <TeamRightRail
          basePath={basePath}
          activeSection="chat"
          isMobile={isCompactLayout}
        />
      ) : null}

      <Snackbar
        open={Boolean(errorMsg)}
        autoHideDuration={6000}
        onClose={() => setErrorMsg("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="error" onClose={() => setErrorMsg("")} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TeamPage;
